/**
 * L'INVITATION À CRÉER UN COMPTE (ROADMAP 6.2).
 *
 * ══ Ce que cet écran doit être, et ce qu'il ne sera jamais ══════════════════
 *
 * Jamais un mur : le §1 promet un jeu jouable en moins de cinq secondes sans compte, et
 * le §3 fait de l'authentification une option. Cette boîte se referme d'un tap, ne bloque
 * aucune action, et ne revient pas avant une semaine (\`core/accountPrompt\`).
 *
 * ⚠️ **L'argument est la SAUVEGARDE, jamais la newsletter.** « Ta ville te suit sur ton
 * téléphone » convertit ; « inscris-toi » non. Le consentement à recevoir des e-mails est
 * une case séparée, décochée par défaut — les confondre serait inefficace et illégal.
 *
 * ══ Pourquoi le formulaire n'est pas réécrit ici ═════════════════════════════
 *
 * \`createAccountSection()\` porte déjà le champ, l'envoi du lien magique, les messages
 * d'erreur et l'état « relié ». En réécrire une copie pour cette boîte donnerait deux
 * formulaires à corriger le jour où l'authentification change — et c'est toujours le
 * second qu'on oublie. La section est donc **instanciée** ici, telle quelle.
 */

import { t } from '@/i18n';
import type { AccountMoment } from '@/core/accountPrompt';
import type { TranslationKey } from '@/i18n';

import { createAccountSection } from './account';
import { createButton, createModal } from './components';
import type { Component } from './components';

export interface AccountInviteHandle extends Component {
  /** Ouvre l'invitation pour ce moment. L'appelant a déjà vérifié qu'il a le droit. */
  open(moment: AccountMoment): void;
  /** Le compte a-t-il été relié depuis l'ouverture ? Sert à ne plus reproposer. */
  destroy(): void;
}

export function createAccountInvite(): AccountInviteHandle {
  const modal = createModal({ title: t('accountInvite.title'), icon: 'shield' });
  document.body.append(modal.el);
  modal.el.classList.add('account-invite');

  /*
    Le texte change avec le moment, parce que l'argument change : après un partage, ce
    qu'on protège est le lien qui circule ; après une ère, c'est un bonus permanent ;
    avant un achat, ce sont des gemmes payées. Un texte unique dirait la même chose à
    trois situations qui n'ont rien à voir.
  */
  const lead = document.createElement('p');
  lead.className = 'account-invite__lead';

  const account = createAccountSection();

  const later = createButton({
    label: t('accountInvite.later'),
    variant: 'ghost',
    onClick: () => {
      modal.close();
    },
  });
  later.el.classList.add('account-invite__later');

  modal.content.append(lead, account.el, later.el);

  return {
    el: modal.el,
    open(moment) {
      lead.textContent = t(`accountInvite.${moment}` as TranslationKey);
      account.refresh();
      modal.open();
    },
    destroy() {
      account.destroy();
      modal.el.remove();
    },
  };
}
